import styled from '@emotion/styled';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Link } from 'react-router';
import { fade_in_right_keyframes } from '../../animations';
import { useAdvancedHover } from '../../hooks/useAdvancedHover';
import { Z_INDEX } from '../../vars';
import { TagBadgeWidget } from '../TagBadgeWidget';
import { MenuItem } from './MenuItemWidget';
import { MenuSubItemsWidget } from './MenuSubItemsWidget';

export interface MenuSubItemWidgetProps {
  item: MenuItem;
}

export const MenuSubItemWidget: React.FC<MenuSubItemWidgetProps> = (props) => {
  const { item } = props;
  const hasSubItems = item.items?.length > 0;
  const itemRef = React.useRef<HTMLDivElement>(null);

  const [isHovering, willHover, willUnhover] = useAdvancedHover(itemRef, {
    enterDelay: 50,
    leaveDelay: 150
  });

  return (
    <S.SubItem ref={itemRef}>
      <S.LinkInline to={item.to}>
        {item.label}
        {props.item.tag ? <TagBadgeWidget sinceVersion={props.item.tagVersion}>{props.item.tag}</TagBadgeWidget> : null}
      </S.LinkInline>
      {hasSubItems && <S.Angle icon={faAngleRight} />}
      {item.icon ? <S.Icon icon={item.icon} /> : null}
      {hasSubItems && isHovering && (
        // Key makes sure that the animation is played again
        <S.Dropdown fadeOut={willUnhover} key={`dropdown-${willUnhover}`}>
          <MenuSubItemsWidget items={item.items} />
        </S.Dropdown>
      )}
    </S.SubItem>
  );
};

namespace S {
  export const Icon = styled(FontAwesomeIcon)`
    padding-left: 10px;
    padding-right: 10px;
  `;
  export const LinkInline = styled(Link)`
    padding: 10px;
    color: ${(p) => p.theme.header.textColor};
    text-decoration: none;
    font-size: 14px;
    flex-grow: 1;
    white-space: nowrap;
    display: flex;
    align-items: center;
    column-gap: 10px;
    justify-content: space-between;
  `;

  export const SubItem = styled.div`
    min-width: 140px;
    display: flex;
    align-items: center;
    column-gap: 5px;
    justify-content: space-between;

    position: relative;

    &:hover {
      background: ${(p) => (p.theme.light ? p.theme.header.dropdownHover : p.theme.header.dropdownSelected)};
      border-radius: 5px;
    }
  `;

  export const Angle = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 12px;
    opacity: 0.5;
  `;

  export const Dropdown = styled.div<{ fadeOut: boolean }>`
    position: absolute;
    animation-name: ${fade_in_right_keyframes};
    animation-direction: ${(p) => (p.fadeOut ? 'reverse' : 'normal')};
    animation-iteration-count: 1;
    animation-timing-function: ease-in;
    animation-duration: 0.2s;

    top: 0;
    left: 100%;
    z-index: ${Z_INDEX.DESKTOP_DROPDOWN};
  `;
}
